// src/components/ProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Loading from './Loading';

const ProtectedRoute = ({ children }) => {
  const { user, loading, initialized, refreshAuthState } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [loadingCount, setLoadingCount] = useState(0);
  
  useEffect(() => {
    // Set a time limit for auth checking
    const timeout = setTimeout(() => {
      if (isChecking && loadingCount > 5) {
        console.log("Auth check timeout exceeded, forcing refresh");
        refreshAuthState();
        setLoadingCount(0);
      }
    }, 3000);
    
    return () => clearTimeout(timeout);
  }, [isChecking, loadingCount, refreshAuthState]);
  
  useEffect(() => {
    if (loading) {
      setLoadingCount(prev => prev + 1);
    }
  }, [loading]);
  
  useEffect(() => {
    // This function checks if we have authentication state
    const checkAuthState = async () => {
      console.log("ProtectedRoute - Checking auth state:", { 
        user: !!user, 
        loading, 
        initialized, // Using 'initialized' instead of 'authInitialized'
        isChecking
      });
      
      // Wait for the auth state to be initialized and not loading
      if (!loading && initialized) {
        if (!user) {
          // If no user is found after initialization, redirect to login
          console.log("ProtectedRoute - No user found, redirecting to login");
          navigate('/login', { replace: true });
        } else {
          console.log("ProtectedRoute - User authenticated:", user.email);
        }
        
        // Finished checking auth state
        setIsChecking(false);
      }
    };
    
    checkAuthState();
  }, [loading, user, initialized, navigate, isChecking]);

  // Show loading state while checking auth
  if (loading || isChecking) {
    return <Loading message="Verifying authentication..." />;
  }
  
  // If there's still no user after checking, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // If authenticated, render the protected content
  return children;
};

export default ProtectedRoute;